/**
 * One person, as named on one organisation's filings.
 *
 * Their names were already all over this site — under "Who filed it" on
 * every organisation page and "The people" on every profile — and every one
 * of them was a dead end. They are also the half of this record nobody else
 * presents: a government job sworn to on a quarterly form, the member of
 * Congress that job names, and the contributions reported against the person
 * who swore it.
 *
 * It is the only page here about a private individual rather than an
 * institution, and the two claims it would make carelessly are both wrong.
 *
 * **That the money is theirs.** It is money reported against their name. The
 * largest such figure in this corpus is $1.8m against one lobbyist at K&L
 * Gates, and every line of it names a contributor of "CONGRESSIONAL BLACK
 * CAUCUS PAC" — a committee whose report he filed rather than cheques he
 * wrote. So the word "gave" appears nowhere on this page about a person, the
 * split between their own report and their employer's is stated in words,
 * and the cheques sit underneath the figure where they can be opened.
 *
 * **That it is one person.** No federal form issues a lobbyist an identifier
 * that survives a change of employer: the LD-2's `lobbyist_id` is per
 * registrant, and a thousand names here are used at two or more firms. So
 * the page is keyed on the pair, links the same name elsewhere, and says it
 * does not know — the reader is given the evidence rather than our guess.
 */

import { Link, useParams } from "react-router-dom";
import { type Cheques, type Lobbyist, api } from "../api";
import { ChequeList } from "../components/Cheques";
import { Problem, Spinner } from "../components/bits";
import { PARTY_NAME, TINT, brief, partyKey } from "../components/party";
import { useAsync } from "../useAsync";

/** Where a member of Congress this person once worked for lives. */
function MemberLink({
  member,
}: {
  member: Lobbyist["members"][number];
}) {
  const key = partyKey(member.party);
  return (
    <Link
      to={`/member/${member.bioguide}`}
      className={`inline-flex items-baseline gap-2 rounded-xl border border-line px-3 py-1.5 text-sm font-medium hover:border-brand ${TINT[key] ?? ""}`}
    >
      {member.full_name ?? member.bioguide}
      <span className="text-xs font-normal text-muted">
        {[PARTY_NAME[key], member.state].filter(Boolean).join(" · ")}
      </span>
    </Link>
  );
}

/**
 * What they swore they used to be.
 *
 * A "covered position" is the disclosure the Lobbying Disclosure Act asks
 * for and nothing else on this site can substitute: the filer names, in
 * their own words, the government job they held before this one. Quoted
 * whole because these strings are careers — three offices and two members is
 * ordinary — and a summary of one is ours rather than theirs.
 */
function WhatTheySwore({ data }: { data: Lobbyist }) {
  if (!data.position) {
    return (
      <section className="mt-8">
        <h2 className="font-display text-xl font-semibold">Before this</h2>
        <p className="mt-2 max-w-prose leading-relaxed text-muted">
          They have sworn to no prior government job on any filing held here, which is true of three
          in four of the people named in this record. The question is only asked of lobbyists
          registered by name on a quarterly LD-2.
        </p>
      </section>
    );
  }
  return (
    <section className="mt-8">
      <h2 className="font-display text-xl font-semibold">Before this</h2>
      <p className="mt-1 text-sm text-muted">
        The government job they swore to on their employer's quarterly filing, in their own words.
      </p>
      <blockquote className="mt-3 border-l-4 border-brand/50 bg-brand-soft/40 py-4 pr-5 pl-5">
        <p className="font-display text-lg leading-relaxed">{data.position}</p>
      </blockquote>
      {data.members.length > 0 && (
        <>
          <p className="mt-4 text-sm text-muted">
            {data.members.length === 1
              ? "That job names somebody still in this corpus."
              : `That job names ${data.members.length} people still in this corpus.`}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {data.members.map((member) => (
              <MemberLink key={member.bioguide} member={member} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * The money, worded so that it survives William Kirk.
 *
 * A total on this page is what their own semi-annual reports carried. That
 * much the record does settle: a lobbyist is named on an LD-203 only where
 * an individual filed it, and not one of the 243,240 rows naming one is
 * marked as the organisation's.
 *
 * What it does not settle is whose money it was, and the gap is not small.
 * Each line names its own contributor, and for a lobbyist who files for a
 * political action committee that contributor is the committee: the largest
 * figure of this kind here is $1.8m against one lobbyist at K&L Gates, every
 * line of which names "CONGRESSIONAL BLACK CAUCUS PAC". That field is not
 * parsed into this site, so the page must never say a person gave anything —
 * it reports what they reported, and puts the rows underneath.
 */
function TheMoney({ data }: { data: Lobbyist }) {
  if (!data.gifts) return null;
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl font-semibold">What their own filings reported</h2>
      <div className="mt-3 rounded-2xl border border-line bg-card p-5 shadow-sm">
        <div className="font-display text-3xl font-semibold tabular-nums">{brief(data.filed)}</div>
        <p className="mt-2 max-w-prose text-sm leading-relaxed text-muted">
          Across {data.gifts.toLocaleString()} {data.gifts === 1 ? "line" : "lines"} of the
          semi-annual contribution reports filed in their name while registered to {data.registrant}
          {data.first_year ? `, ${data.first_year} to ${data.last_year}` : ""}. The register names a
          lobbyist only on a report an individual made, rather than one their employer filed.
        </p>
        <p className="mt-3 max-w-prose text-sm leading-relaxed text-muted">
          None of which makes the money theirs. Every line names its own contributor, and for
          somebody who files on behalf of a political action committee that contributor is the
          committee — the largest figure of this kind in the record, $1.8m against one lobbyist, is
          a caucus PAC's money from end to end. This site does not yet read that field, so the lines
          below and the forms they came off are the answer here, not the number above.
        </p>
      </div>
    </section>
  );
}

/**
 * The same spelling at other organisations.
 *
 * Presented as a question rather than as a career. Michael Williams files at
 * seven firms in this record; he may be one man who moved six times or seven
 * men, and nothing anybody publishes decides it.
 */
function TheSameNameElsewhere({ data }: { data: Lobbyist }) {
  if (data.elsewhere.length === 0) return null;
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl font-semibold">This name at other organisations</h2>
      <p className="mt-1 max-w-prose text-sm leading-relaxed text-muted">
        No federal form gives a lobbyist an identifier that survives a change of employer, so
        whether this is the same person is not something the filings answer. Nothing from these
        pages is counted in the figures above.
      </p>
      <ol className="mt-3 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card shadow-sm">
        {data.elsewhere.map((other) => (
          <li key={other.registrant_id} className="flex items-baseline gap-3 px-4 py-3 text-sm">
            <span className="w-20 font-medium tabular-nums">{brief(other.filed)}</span>
            <Link
              to={`/money/${other.registrant_id}/lobbyist/${other.slug}`}
              className="flex-1 font-medium text-brand hover:underline"
            >
              {other.registrant}
            </Link>
            <span className="text-faint">
              {other.gifts.toLocaleString()} {other.gifts === 1 ? "line" : "lines"}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** The page as a pure render, so its sentences can be tested without a
 *  network. What this page must not say is the part worth testing. */
export function LobbyistProfile({ data, cheques }: { data: Lobbyist; cheques: Cheques }) {
  return (
    <div className="mx-auto max-w-4xl px-5 py-10">
      <Link to={`/money/${data.registrant_id}`} className="text-sm text-brand hover:underline">
        ← {data.registrant}
      </Link>
      <h1 className="mt-3 font-display text-3xl font-semibold">{data.name}</h1>
      <p className="mt-1.5 max-w-prose text-muted">
        As named on filings by{" "}
        <Link to={`/money/${data.registrant_id}`} className="text-brand hover:underline">
          {data.registrant}
        </Link>
        {data.first_year ? `, ${data.first_year} to ${data.last_year}` : ""}. Everything here is off
        a form they or their employer swore to.
      </p>

      <WhatTheySwore data={data} />
      <TheMoney data={data} />

      {cheques.total > 0 && (
        <section className="mt-8">
          <h2 className="font-display text-xl font-semibold">Every line, and the form it came off</h2>
          {/* The organisation, not the person. The list's footnote explains
              that "personally" means the employer on the form gave nothing,
              and handed this page's subject it reads as the absurd "Michael
              Williams, with Michael Williams as their employer". */}
          <ChequeList page={cheques} name={data.registrant} />
        </section>
      )}

      <TheSameNameElsewhere data={data} />
    </div>
  );
}

export function LobbyistPage() {
  const { registrantId, key } = useParams();
  const id = Number(registrantId);
  const slug = key ?? "";

  const person = useAsync(() => api.lobbyist(id, slug), [id, slug]);
  // Fetched beside the profile rather than under it: the rows are what
  // qualifies the total, and a page that renders a figure first and its
  // evidence a moment later has already made the claim.
  const cheques = useAsync(
    () => api.lobbyistGifts(id, slug).catch(() => ({ gifts: [], total: 0 })),
    [id, slug],
  );

  if (person.error) return <Problem error={person.error} retry={person.reload} />;
  if (person.loading || !person.data) return <Spinner label="Reading the filings…" />;

  return (
    <LobbyistProfile data={person.data} cheques={cheques.data ?? { gifts: [], total: 0 }} />
  );
}
